import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Autocomplete,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  TrendingDown as StockOutIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Save as SaveIcon,
  ShoppingBag as SalesIcon,
  Person as CustomerIcon,
  Receipt as InvoiceIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
  BrokenImage as DamageIcon,
  Science as ExpiredIcon,
  Security as TheftIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";

const StockOut = () => {
  // Sample customers data
  const customers = [
    { id: 1, code: "CUST001", name: "Rajesh Kumar", phone: "+91-9876543210" },
    { id: 2, code: "CUST002", name: "Priya Sharma", phone: "+91-9876543211" },
    { id: 3, code: "CUST003", name: "Amit Patel", phone: "+91-9876543212" },
    { id: 4, code: "CUST004", name: "Sunita Reddy", phone: "+91-9876543213" },
    { id: 5, code: "WALK-IN", name: "Walk-In Customer", phone: "-" },
  ];

  // Sample products data with stock levels
  const products = [
    { id: 1, code: "PRD001", name: "Samsung Galaxy S23", category: "Electronics", unit: "Piece", stock: 45, rate: 55000 },
    { id: 2, code: "PRD002", name: "Apple iPhone 14", category: "Electronics", unit: "Piece", stock: 30, rate: 75000 },
    { id: 3, code: "PRD003", name: "Sony LED TV 55\"", category: "Electronics", unit: "Piece", stock: 15, rate: 45000 },
    { id: 4, code: "PRD004", name: "HP Laptop i5", category: "Electronics", unit: "Piece", stock: 25, rate: 42000 },
    { id: 5, code: "PRD005", name: "Nike Sports Shoes", category: "Clothing", unit: "Pair", stock: 60, rate: 4500 },
    { id: 6, code: "PRD006", name: "Adidas T-Shirt", category: "Clothing", unit: "Piece", stock: 120, rate: 1200 },
    { id: 7, code: "PRD007", name: "Levi's Jeans", category: "Clothing", unit: "Piece", stock: 80, rate: 2500 },
    { id: 8, code: "PRD008", name: "LG Refrigerator 190L", category: "Home Appliances", unit: "Piece", stock: 12, rate: 18000 },
    { id: 9, code: "PRD009", name: "Microwave Oven", category: "Home Appliances", unit: "Piece", stock: 18, rate: 8500 },
    { id: 10, code: "PRD010", name: "Rice 1kg", category: "Groceries", unit: "Kg", stock: 500, rate: 60 },
  ];

  // Stock out entries data
  const [stockOutData, setStockOutData] = useState([
    {
      id: 1,
      stockOutNo: "SO-2024-001",
      stockOutDate: "2024-01-15",
      transactionType: "Sales",
      customerName: "Rajesh Kumar",
      referenceNo: "INV-2024-001",
      totalItems: 3,
      totalQuantity: 5,
      totalAmount: 142500,
      remarks: "Regular sale",
      status: "Completed",
      createdDate: "2024-01-15",
    },
    {
      id: 2,
      stockOutNo: "SO-2024-002",
      stockOutDate: "2024-01-18",
      transactionType: "Damage",
      customerName: "-",
      referenceNo: "DMG-001",
      totalItems: 2,
      totalQuantity: 3,
      totalAmount: 15600,
      remarks: "Water damage during storage",
      status: "Completed",
      createdDate: "2024-01-18",
    },
    {
      id: 3,
      stockOutNo: "SO-2024-003",
      stockOutDate: "2024-01-20",
      transactionType: "Sales",
      customerName: "Walk-In Customer",
      referenceNo: "INV-2024-002",
      totalItems: 4,
      totalQuantity: 8,
      totalAmount: 89500,
      remarks: "Cash sale",
      status: "Completed",
      createdDate: "2024-01-20",
    },
    {
      id: 4,
      stockOutNo: "SO-2024-004",
      stockOutDate: "2024-01-22",
      transactionType: "Expired",
      customerName: "-",
      referenceNo: "EXP-001",
      totalItems: 1,
      totalQuantity: 50,
      totalAmount: 3000,
      remarks: "Expired grocery items",
      status: "Completed",
      createdDate: "2024-01-22",
    },
    {
      id: 5,
      stockOutNo: "SO-2024-005",
      stockOutDate: "2024-01-25",
      transactionType: "Sample",
      customerName: "-",
      referenceNo: "SMP-001",
      totalItems: 2,
      totalQuantity: 10,
      totalAmount: 17000,
      remarks: "Product samples for promotion",
      status: "Draft",
      createdDate: "2024-01-25",
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [editingStockOut, setEditingStockOut] = useState(null);
  const [viewingStockOut, setViewingStockOut] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Stock out form state
  const [stockOutForm, setStockOutForm] = useState({
    stockOutNo: "",
    stockOutDate: new Date().toISOString().split('T')[0],
    transactionType: "Sales",
    customerId: null,
    referenceNo: "",
    remarks: "",
    status: "Draft",
  });

  // Stock out items state
  const [stockOutItems, setStockOutItems] = useState([
    {
      id: Date.now(),
      productId: null,
      productName: "",
      availableStock: 0,
      quantity: 1,
      rate: 0,
      amount: 0,
      reason: "",
    }
  ]);

  // Helper functions
  const getFilteredStockOuts = () => {
    return stockOutData.filter((stockOut) => {
      const matchesSearch = Object.values(stockOut).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesStatus = statusFilter === "all" || stockOut.status === statusFilter;
      const matchesType = typeFilter === "all" || stockOut.transactionType === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Completed':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Draft':
        return { backgroundColor: '#fff3e0', color: '#e65100' };
      case 'Cancelled':
        return { backgroundColor: '#ffebee', color: '#d32f2f' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Sales': return 'success';
      case 'Damage': return 'error';
      case 'Theft': return 'error';
      case 'Expired': return 'warning';
      case 'Sample': return 'info';
      case 'Return to Vendor': return 'secondary';
      case 'Transfer': return 'primary';
      case 'Other': return 'default';
      default: return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Sales': return <SalesIcon />;
      case 'Damage': return <DamageIcon />;
      case 'Theft': return <TheftIcon />;
      case 'Expired': return <ExpiredIcon />;
      case 'Sample': return <InventoryIcon />;
      default: return <StockOutIcon />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate item amount
  const calculateItemAmount = (item) => {
    return item.quantity * item.rate;
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalAmount = 0;
    let totalQuantity = 0;
    let totalItems = 0;

    stockOutItems.forEach(item => {
      if (item.productId) {
        totalAmount += item.amount;
        totalQuantity += item.quantity;
        totalItems++;
      }
    });

    return {
      totalAmount,
      totalQuantity,
      totalItems,
    };
  };

  // Handlers
  const handleEdit = (stockOut) => {
    setEditingStockOut(stockOut);
    setOpenModal(true);
  };

  const handleView = (stockOut) => {
    setViewingStockOut(stockOut);
    setOpenViewModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this stock out entry?")) {
      setStockOutData(stockOutData.filter((stockOut) => stockOut.id !== id));
      setSuccessMessage("Stock out entry deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    setSuccessMessage(`Search completed. Found ${getFilteredStockOuts().length} entries.`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setTypeFilter("all");
    setPage(0);
    setErrors({});
    setSuccessMessage("");
  };

  const handleOpenModal = () => {
    setEditingStockOut(null);
    const newStockOutNo = `SO-${new Date().getFullYear()}-${String(stockOutData.length + 1).padStart(3, '0')}`;
    setStockOutForm({
      stockOutNo: newStockOutNo,
      stockOutDate: new Date().toISOString().split('T')[0],
      transactionType: "Sales",
      customerId: null,
      referenceNo: "",
      remarks: "",
      status: "Draft",
    });
    setStockOutItems([
      {
        id: Date.now(),
        productId: null,
        productName: "",
        availableStock: 0,
        quantity: 1,
        rate: 0,
        amount: 0,
        reason: "",
      }
    ]);
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingStockOut(null);
    setErrors({});
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setViewingStockOut(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setStockOutForm({ ...stockOutForm, [name]: value });
    
    // Auto-generate reference number based on transaction type
    if (name === "transactionType") {
      let prefix = "";
      switch (value) {
        case "Sales": prefix = "INV"; break;
        case "Damage": prefix = "DMG"; break;
        case "Theft": prefix = "THF"; break;
        case "Expired": prefix = "EXP"; break;
        case "Sample": prefix = "SMP"; break;
        case "Return to Vendor": prefix = "RTV"; break;
        case "Transfer": prefix = "TRF"; break;
        default: prefix = "REF";
      }
      const refNo = `${prefix}-${String(stockOutData.length + 1).padStart(3, '0')}`;
      setStockOutForm(prev => ({ ...prev, referenceNo: refNo }));
    }
  };

  const handleCustomerChange = (event, newValue) => {
    setStockOutForm({ 
      ...stockOutForm, 
      customerId: newValue ? newValue.id : null 
    });
  };

  const handleProductChange = (index, event, newValue) => {
    const updatedItems = [...stockOutItems];
    if (newValue) {
      updatedItems[index] = {
        ...updatedItems[index],
        productId: newValue.id,
        productName: newValue.name,
        availableStock: newValue.stock,
        rate: newValue.rate,
      };
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        productId: null,
        productName: "",
        availableStock: 0,
        rate: 0,
      };
    }
    setStockOutItems(updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...stockOutItems];
    updatedItems[index][field] = parseFloat(value) || 0;
    
    // Validate quantity against available stock
    if (field === 'quantity' && updatedItems[index].quantity > updatedItems[index].availableStock) {
      setErrors({ 
        ...errors, 
        [`item_${index}`]: `Only ${updatedItems[index].availableStock} units available in stock` 
      });
    } else {
      const newErrors = { ...errors };
      delete newErrors[`item_${index}`];
      setErrors(newErrors);
    }
    
    updatedItems[index].amount = calculateItemAmount(updatedItems[index]);
    setStockOutItems(updatedItems);
  };

  const handleAddItem = () => {
    setStockOutItems([
      ...stockOutItems,
      {
        id: Date.now(),
        productId: null,
        productName: "",
        availableStock: 0,
        quantity: 1,
        rate: 0,
        amount: 0,
        reason: "",
      }
    ]);
  };

  const handleRemoveItem = (index) => {
    if (stockOutItems.length > 1) {
      const updatedItems = stockOutItems.filter((_, i) => i !== index);
      setStockOutItems(updatedItems);
    }
  };

  const handleSaveStockOut = async (isDraft = false) => {
    // Validation
    if (stockOutForm.transactionType === "Sales" && !stockOutForm.customerId) {
      setErrors({ customer: "Please select a customer for sales transaction" });
      return;
    }
    if (!stockOutForm.referenceNo) {
      setErrors({ reference: "Please enter reference number" });
      return;
    }
    const validItems = stockOutItems.filter(item => item.productId);
    if (validItems.length === 0) {
      setErrors({ items: "Please add at least one product" });
      return;
    }
    // Check stock availability
    for (let i = 0; i < validItems.length; i++) {
      if (validItems[i].quantity > validItems[i].availableStock) {
        setErrors({ items: `Insufficient stock for ${validItems[i].productName}` });
        return;
      }
    }

    setIsLoading(true);
    try {
      const totals = calculateTotals();
      let customerName = "-";
      if (stockOutForm.customerId) {
        const customer = customers.find(c => c.id === stockOutForm.customerId);
        customerName = customer.name;
      }

      const newStockOut = {
        id: stockOutData.length + 1,
        stockOutNo: stockOutForm.stockOutNo,
        stockOutDate: stockOutForm.stockOutDate,
        transactionType: stockOutForm.transactionType,
        customerName: customerName,
        referenceNo: stockOutForm.referenceNo,
        totalItems: totals.totalItems,
        totalQuantity: totals.totalQuantity,
        totalAmount: totals.totalAmount,
        remarks: stockOutForm.remarks,
        status: isDraft ? "Draft" : "Completed",
        createdDate: new Date().toISOString().split('T')[0],
      };

      setStockOutData([newStockOut, ...stockOutData]);
      setSuccessMessage(`Stock out ${isDraft ? 'draft saved' : 'entry completed'} successfully!`);
      handleCloseModal();
    } catch (error) {
      setErrors({ general: "An error occurred. Please try again." });
    } finally {
      setIsLoading(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const filteredStockOuts = getFilteredStockOuts();
  const totals = calculateTotals();

  // Get unique transaction types
  const transactionTypes = [...new Set(stockOutData.map(s => s.transactionType))];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StockOutIcon sx={{ color: 'error.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Stock Out / Sales Adjustment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track outgoing inventory - sales, damages, and adjustments • {filteredStockOuts.length} entries found
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          New Stock Out Entry
        </Button>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Stock Out Entries..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              label="Transaction Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Types</option>
              {transactionTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Status</option>
              <option value="Completed">Completed</option>
              <option value="Draft">Draft</option>
              <option value="Cancelled">Cancelled</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ flex: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                sx={{ flex: 1 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Stock Out No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Customer/Ref</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Reference No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Items/Qty</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Total Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStockOuts.length > 0 ? (
                filteredStockOuts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((stockOut, index) => (
                    <TableRow 
                      key={stockOut.id} 
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'monospace' }}>
                          {stockOut.stockOutNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {stockOut.stockOutDate}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={getTypeIcon(stockOut.transactionType)}
                          label={stockOut.transactionType} 
                          size="small" 
                          color={getTypeColor(stockOut.transactionType)}
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {stockOut.customerName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {stockOut.referenceNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {stockOut.totalItems} items
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {stockOut.totalQuantity} qty
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="700" color="error.main">
                          {formatCurrency(stockOut.totalAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={stockOut.status}
                          size="small"
                          sx={{
                            ...getStatusStyle(stockOut.status),
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(stockOut)}
                              sx={{
                                color: 'info.main',
                                '&:hover': { backgroundColor: 'info.light' },
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(stockOut)}
                              sx={{
                                color: 'warning.main',
                                '&:hover': { backgroundColor: 'warning.light' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(stockOut.id)}
                              sx={{
                                color: 'error.main',
                                '&:hover': { backgroundColor: 'error.light' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <StockOutIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No stock out entries found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredStockOuts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Stock Out Entry Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xl" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <StockOutIcon />
            {editingStockOut ? 'Edit Stock Out Entry' : 'New Stock Out Entry'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {/* Stock Out Header Information */}
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#fff3f3' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="error">
              Stock Out Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Stock Out No"
                  name="stockOutNo"
                  value={stockOutForm.stockOutNo}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <InvoiceIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Stock Out Date"
                  name="stockOutDate"
                  type="date"
                  value={stockOutForm.stockOutDate}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Transaction Type *</InputLabel>
                  <Select
                    name="transactionType"
                    value={stockOutForm.transactionType}
                    onChange={handleFormChange}
                    label="Transaction Type *"
                  >
                    <MenuItem value="Sales">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SalesIcon /> Sales
                      </Box>
                    </MenuItem>
                    <MenuItem value="Damage">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DamageIcon /> Damage / Defective
                      </Box>
                    </MenuItem>
                    <MenuItem value="Theft">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TheftIcon /> Theft / Loss
                      </Box>
                    </MenuItem>
                    <MenuItem value="Expired">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ExpiredIcon /> Expired
                      </Box>
                    </MenuItem>
                    <MenuItem value="Sample">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InventoryIcon /> Sample / Demo
                      </Box>
                    </MenuItem>
                    <MenuItem value="Return to Vendor">Return to Vendor</MenuItem>
                    <MenuItem value="Transfer">Transfer to Another Location</MenuItem>
                    <MenuItem value="Other">Other Adjustment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {stockOutForm.transactionType === "Sales" && (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => `${option.code} - ${option.name}`}
                    onChange={handleCustomerChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Customer *"
                        error={!!errors.customer}
                        helperText={errors.customer}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <CustomerIcon />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} sm={stockOutForm.transactionType === "Sales" ? 6 : 12}>
                <TextField
                  fullWidth
                  label="Reference No *"
                  name="referenceNo"
                  value={stockOutForm.referenceNo}
                  onChange={handleFormChange}
                  error={!!errors.reference}
                  helperText={errors.reference || "Invoice/Document reference number"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <InvoiceIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Product Items Table */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                Product Items
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                size="small"
                color="error"
              >
                Add Item
              </Button>
            </Box>
            {errors.items && (
              <Alert severity="error" sx={{ mb: 2 }}>{errors.items}</Alert>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Available Stock</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Rate</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '8%' }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockOutItems.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Autocomplete
                          options={products}
                          getOptionLabel={(option) => `${option.code} - ${option.name} (Stock: ${option.stock})`}
                          onChange={(event, newValue) => handleProductChange(index, event, newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Product"
                              size="small"
                              fullWidth
                            />
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${item.availableStock} units`}
                          size="small"
                          color={item.availableStock < 10 ? "error" : item.availableStock < 50 ? "warning" : "success"}
                          icon={item.availableStock < 10 ? <WarningIcon /> : null}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          inputProps={{ min: 1, max: item.availableStock, step: 1 }}
                          fullWidth
                          error={!!errors[`item_${index}`]}
                          helperText={errors[`item_${index}`]}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                          inputProps={{ min: 0, step: 0.01 }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="error.main">
                          {formatCurrency(item.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveItem(index)}
                          disabled={stockOutItems.length === 1}
                          color="error"
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Remarks and Totals */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  Additional Information
                </Typography>
                <TextField
                  fullWidth
                  label="Remarks / Notes"
                  name="remarks"
                  value={stockOutForm.remarks}
                  onChange={handleFormChange}
                  multiline
                  rows={6}
                  placeholder={
                    stockOutForm.transactionType === "Damage" ? "Describe the damage..." :
                    stockOutForm.transactionType === "Expired" ? "Expiry details..." :
                    stockOutForm.transactionType === "Theft" ? "Incident details..." :
                    stockOutForm.transactionType === "Sample" ? "Sample distribution details..." :
                    "Additional notes..."
                  }
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, backgroundColor: '#fff3f3', height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="error">
                  Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Total Items:</Typography>
                    <Typography variant="body1" fontWeight="600">
                      {totals.totalItems}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Total Quantity:</Typography>
                    <Typography variant="body1" fontWeight="600">
                      {totals.totalQuantity} units
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="h6" fontWeight="bold">Total Amount:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="error.main">
                      {formatCurrency(totals.totalAmount)}
                    </Typography>
                  </Box>
                  {stockOutForm.transactionType !== "Sales" && (
                    <Alert severity="warning" icon={<WarningIcon />}>
                      Stock value loss: {formatCurrency(totals.totalAmount)}
                    </Alert>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {errors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button onClick={handleCloseModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleSaveStockOut(true)}
            disabled={isLoading}
            startIcon={<SaveIcon />}
          >
            Save as Draft
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSaveStockOut(false)}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            {isLoading ? 'Processing...' : 'Complete Stock Out'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Stock Out Modal */}
      <Dialog open={openViewModal} onClose={handleCloseViewModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon />
            Stock Out Entry Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {viewingStockOut && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Stock Out No</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    {viewingStockOut.stockOutNo}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Stock Out Date</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingStockOut.stockOutDate}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Transaction Type</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      icon={getTypeIcon(viewingStockOut.transactionType)}
                      label={viewingStockOut.transactionType} 
                      color={getTypeColor(viewingStockOut.transactionType)}
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Customer/Reference</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingStockOut.customerName}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Reference No</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    {viewingStockOut.referenceNo}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Total Items / Quantity</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingStockOut.totalItems} items / {viewingStockOut.totalQuantity} qty
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(233, 30, 99, 0.1)' }}>
                  <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }} color="error.main">
                    {formatCurrency(viewingStockOut.totalAmount)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(233, 30, 99, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={viewingStockOut.status}
                      sx={{
                        ...getStatusStyle(viewingStockOut.status),
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Remarks</Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {viewingStockOut.remarks || 'No remarks'}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseViewModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockOut;

